import { useEffect, useLayoutEffect } from "react";
import { Spinner } from "@fluentui/react-components";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { RecordDrawer } from "@/features/records/RecordDrawer";
import { CloseReportDialog } from "@/features/reports/CloseReportDialog";
import { WbsDeleteConfirmDialog } from "@/features/schedule/WbsDeleteConfirmDialog";
import { SchedulePeekDrawer } from "@/features/docs/SchedulePeekDrawer";
import { ExecutionTaskDrawer } from "@/features/tasks/ExecutionTaskDrawer";
import { PlannerSettingsDialog } from "@/features/tasks/PlannerSettingsDialog";
import { PlanInfoPanel } from "@/features/access/PlanInfoPanel";
import { BlockedEditHint, PlanReadOnlyBanner } from "@/features/access/ReadOnlyBanner";
import { showInPlanList, usePlanVisibility } from "@/features/access/plan-visibility";
import { useCan, useWorkspaceAdminView } from "@/features/access/use-plan-access";
import { useWorkspaceRole } from "@/features/access/workspace-access";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { rememberPlanTab } from "@/shell/plan-tabs";
import { planHref, tabFromPath } from "@/shell/project-nav";
import { normalizeProjectTab } from "@/store/persist";
import { PlanUnavailable } from "./PlanUnavailable";

export function ProjectLayout() {
  const { id } = useParams();
  const location = useLocation();
  const exists = useCatalogStore((s) => (id ? !!s.projects[id] : false));
  const activeId = useProjectStore((s) => s.activeId);
  const openProject = useProjectStore((s) => s.openProject);
  const setLastTab = useProjectStore((s) => s.setLastTab);
  const knownTab = normalizeProjectTab(location.pathname.split("/").filter(Boolean)[2]);
  const tab = knownTab ?? tabFromPath(location.pathname);
  const canReadCost = useCan("plan.cost.read");
  const adminView = useWorkspaceAdminView();
  const listed = showInPlanList(usePlanVisibility(id), useWorkspaceRole());

  useEffect(() => {
    if (id && exists && listed.shown && activeId !== id) openProject(id);
  }, [id, exists, listed.shown, activeId, openProject]);

  useLayoutEffect(() => {
    if (id && exists && listed.shown) rememberPlanTab(id);
  }, [id, exists, listed.shown]);

  useEffect(() => {
    if (id && activeId === id && knownTab) setLastTab(knownTab);
  }, [id, activeId, knownTab, setLastTab]);

  if (!id || !exists || !listed.shown) return <PlanUnavailable />;
  if (adminView === "meta") {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <PlanInfoPanel planId={id} />
      </div>
    );
  }
  if (tab === "budget" && !canReadCost) return <Navigate to={planHref(id!, "reports")} replace />;
  if (activeId !== id) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Spinner label="플랜 여는 중…" />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <PlanReadOnlyBanner />
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Outlet />
      </div>
      <BlockedEditHint />
      <ExecutionTaskDrawer />
      <RecordDrawer />
      <SchedulePeekDrawer />
      <WbsDeleteConfirmDialog />
      <PlannerSettingsDialog />
      <CloseReportDialog />
    </div>
  );
}
